import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import UnreadBadge from 'components/UnreadBadge'
import { pluralize } from 'utils/pluralization-util'
import { DocumentManagementViewModel } from '../../DocumentManagementViewModel'
import styles from './PrimaryControls.sass'

interface PrimaryControlsProps {
  vm: DocumentManagementViewModel
  filterCount: number
  value: string
  onChange: (val: string) => void
  onShowFilterControlsClick: () => void
}

export default observer(function PrimaryControls({
  vm,
  filterCount,
  value,
  onChange,
  onShowFilterControlsClick,
}: PrimaryControlsProps) {
  const change = React.useCallback(
    (e: any) => {
      onChange(e.target.value)
    },
    [onChange],
  )

  const hasFilter = filterCount > 0
  const filterTooltip = hasFilter
    ? `${filterCount} ${pluralize('filter', filterCount)} selected`
    : 'Filter'
  const menuTooltip = 'Menu'

  return (
    <div className={styles.primaryControls}>
      <div className={styles.search}>
        <Icon name="search" />
        <input
          type="text"
          value={value}
          placeholder="Search"
          className={styles.searchInput}
          data-test="document-search-field"
          onChange={change}
        />
      </div>
      <div className={styles.actions}>
        <div className={styles.actionButton}>
          <Tooltip content={filterTooltip} placement="bottom">
            <Button
              onClick={onShowFilterControlsClick}
              size="baseline"
              type="text"
              dataTest={'document-filter-controls-toggle'}
            >
              <Icon color="var(--grey600)" name="filter" />
              {filterCount > 0 && (
                <UnreadBadge
                  className={styles.filterCount}
                  count={filterCount}
                />
              )}
            </Button>
          </Tooltip>
        </div>
        <div className={styles.actionButton}>
          <Dropdown
            autoDismiss
            position="bottom-end"
            disabled={vm.selectedDocumentIds.size < 1}
            button={
              <Tooltip content={menuTooltip} placement="bottom">
                <Button
                  size="baseline"
                  type="text"
                  disabled={vm.selectedDocumentIds.size < 1}
                  dataTest={'document-management-table-menu-toggle'}
                >
                  <Icon color="var(--grey600)" name="more" />
                </Button>
              </Tooltip>
            }
          >
            <div className={styles.tableControlsContainer}>
              <DropdownTitle>More</DropdownTitle>
              <DropdownItem
                icon={<Icon name="download" size={24} />}
                onClick={() => vm.downloadDocuments()}
                dataTest={'document-management-table-menu-download'}
              >
                Download selected
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="delete" size={24} />}
                onClick={() => vm.archiveDocuments()}
                dataTest={'document-management-table-menu-delete'}
              >
                Delete selected
              </DropdownItem>
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  )
})
